import type { SpellcheckResponse } from './spellcheck'
import { sanitizeSearchQuery } from '#shared/utils/search'
import { pickHighestFreq } from './spellcheck-utils'

type Suggestion = NonNullable<SpellcheckResponse['suggestions']>[number]

export type SearchDidYouMeanKind = 'word_replacement' | 'collation_fallback' | 'added_space'
export interface SearchDidYouMeanSuggestion {
  query: string
  kind: SearchDidYouMeanKind
}

export function getSpellcheckSuggestion(
  searchTerm: string,
  spellcheck: SpellcheckResponse | null | undefined,
): string | null {
  return getDidYouMeanSuggestion(searchTerm, spellcheck)?.query ?? null
}

export function getDidYouMeanSuggestion(
  searchTerm: string,
  spellcheck: SpellcheckResponse | null | undefined,
): SearchDidYouMeanSuggestion | null {
  if (!spellcheck || spellcheck.correctlySpelled) {
    return null
  }

  const sanitizedTerm = sanitizeSearchQuery(searchTerm)
  const suggestion = getPerWordSuggestion(sanitizedTerm, spellcheck.suggestions)
  if (suggestion) {
    return { query: suggestion, kind: 'word_replacement' }
  }

  if (hasOriginalFrequencyEvidence(spellcheck.suggestions)) {
    return null
  }

  const collation = getCollationSuggestion(sanitizedTerm, spellcheck.collations)
  return collation ? { query: collation, kind: 'collation_fallback' } : null
}

function getPerWordSuggestion(
  sanitizedTerm: string,
  suggestions: Suggestion[] | null | undefined,
): string | null {
  if (!suggestions?.length) {
    return null
  }

  const didYouMeanMap = new Map<string, string>()
  for (const { term, detail } of suggestions) {
    const best = pickHighestFreq(detail.suggestion, detail.origFreq)
    if (best) {
      didYouMeanMap.set(term, best)
    }
  }

  if (didYouMeanMap.size === 0) {
    return null
  }

  const corrected = sanitizedTerm.split(/\s+/).map((word) => {
    const lookupWord = word.replace(/"/g, '')
    return didYouMeanMap.get(lookupWord) ?? word
  })
  const suggestion = corrected.join(' ')

  return suggestion === sanitizedTerm ? null : suggestion
}

function getCollationSuggestion(
  sanitizedTerm: string,
  collations: SpellcheckResponse['collations'],
): string | null {
  const suggestion = collations?.[0]?.collationQuery ?? null
  return suggestion && suggestion !== sanitizedTerm ? suggestion : null
}

function hasOriginalFrequencyEvidence(suggestions: Suggestion[] | null | undefined): boolean {
  return suggestions?.some(({ detail }) => typeof detail.origFreq === 'number') ?? false
}
